import React, { useEffect, useState } from "react";
import Select from "react-select";
import ContainerDashboard from "../../components/layouts/ContainerDashboard";
import Loader from "../Loader";
import swal from "sweetalert";
import { axiosInstance } from "../Config";
import Pagination, { PerPageSelect } from "../../components/Pagination";
import aiKeyImg from "../../Assets/Images/ai.png";
import { usePermissionData } from "../../AppContext";

/* ─── Constants ─── */
const PROVIDERS = [
  {
    label: "Anthropic",
    value: "Anthropic",
    prefix: "anthropic",
    color: "#c96a2a",
    bg: "#fdf3ec",
  },
  {
    label: "Google",
    value: "Google",
    prefix: "google",
    color: "#4285F4",
    bg: "#edf3ff",
  },
  {
    label: "OpenAI",
    value: "OpenAI",
    prefix: "openai",
    color: "#10a37f",
    bg: "#edfaf5",
  },
];

const FALLBACK_MODELS = {
  Anthropic: [
    { id: "claude-opus-4-7", name: "Claude Opus 4.7" },
    { id: "claude-sonnet-4-6", name: "Claude Sonnet 4.6" },
    { id: "claude-haiku-4-5-20251001", name: "Claude Haiku 4.5" },
  ],
  Google: [
    { id: "gemini-2.5-pro", name: "Gemini 2.5 Pro" },
    { id: "gemini-2.5-flash", name: "Gemini 2.5 Flash" },
    { id: "gemini-2.0-flash", name: "Gemini 2.0 Flash" },
    { id: "gemini-1.5-pro", name: "Gemini 1.5 Pro" },
    { id: "gemini-1.5-flash", name: "Gemini 1.5 Flash" },
    { id: "veo-3.1-generate-preview", name: "Veo 3.1 (Video Generation)" },
    { id: "veo-3.0-generate-preview", name: "Veo 3.0 (Video Generation)" },
    { id: "veo-2.0-generate-001", name: "Veo 2 (Video Generation)" },
  ],
  OpenAI: [
    { id: "gpt-4o", name: "GPT-4o" },
    { id: "gpt-4o-mini", name: "GPT-4o Mini" },
    { id: "o1", name: "o1" },
    { id: "o3-mini", name: "o3 Mini" },
    { id: "gpt-image-1", name: "GPT Image 1 (Image Generation)" },
    { id: "dall-e-3", name: "DALL-E 3 (Image Generation)" },
    { id: "sora", name: "Sora (Video Generation)" },
  ],
};

const dummyStaff = [
  { value: 1, label: "Alice Carter", email: "alice.carter@mindora.com" },
  { value: 2, label: "Ben Thompson", email: "ben.thompson@mindora.com" },
  { value: 3, label: "Clara Hughes", email: "clara.hughes@mindora.com" },
  { value: 4, label: "David Patel", email: "david.patel@mindora.com" },
  { value: 5, label: "Eva Martínez", email: "eva.martinez@mindora.com" },
];

/* ─── Helpers ─── */
const maskKey = (key) =>
  key ? `${key.slice(0, 6)}${"•".repeat(14)}${key.slice(-4)}` : "—";

const formatTokens = (n) => {
  if (!n) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
};

const getProvider = (name) => PROVIDERS.find((p) => p.value === name) || null;

const labelStyle = {
  fontSize: "11px",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: "#999",
};

const EMPTY_FORM = {
  description: "",
  providerName: "",
  modelName: "",
  apiKey: "",
};

const PROVIDER_STEPS = {
  Anthropic: {
    color: "#c96a2a",
    bg: "#fdf3ec",
    border: "#f5c8a0",
    icon: "🤖",
    url: "https://console.anthropic.com",
    urlLabel: "console.anthropic.com",
    steps: [
      "Go to console.anthropic.com and sign in or create a free account.",
      'In the left sidebar, click "API Keys".',
      'Click "Create Key", give it a name, then click "Create Key" to confirm.',
      "Copy the generated key immediately — it will not be shown again.",
      'Paste the key in the "API Key" field below.',
    ],
  },
  Google: {
    color: "#4285F4",
    bg: "#edf3ff",
    border: "#b6d0ff",
    icon: "✨",
    url: "https://aistudio.google.com/app/apikey",
    urlLabel: "aistudio.google.com",
    steps: [
      "Go to aistudio.google.com and sign in with your Google account.",
      'Click "Get API key" in the left sidebar.',
      'Click "Create API key in new project" (or choose an existing project).',
      "Your key is generated instantly — click the copy icon next to it.",
      'Paste the key in the "API Key" field below.',
    ],
  },
  OpenAI: {
    color: "#10a37f",
    bg: "#edfaf5",
    border: "#9ee8d1",
    icon: "💡",
    url: "https://platform.openai.com/api-keys",
    urlLabel: "platform.openai.com",
    steps: [
      "Go to platform.openai.com and sign in or create an account.",
      'Click your profile icon (top-right) → select "API keys".',
      'Click "Create new secret key", give it a name and set permissions.',
      'Click "Create secret key" and copy the key immediately — it won\'t be shown again.',
      'Paste the key in the "API Key" field below.',
    ],
  },
};

/* ─── Component ─── */
const ManageAIKey = () => {
  const { checkAccess } = usePermissionData();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' | 'edit'
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [showKey, setShowKey] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [allModels, setAllModels] = useState({});
  const [modelsLoading, setModelsLoading] = useState(false);
  const [filteredModels, setFilteredModels] = useState([]);

  const [staffList, setStaffList] = useState([]);
  const [alertRecipients, setAlertRecipients] = useState([]);
  const [alertSaving, setAlertSaving] = useState(false);

  const [assignments, setAssignments] = useState({});

  const isEdit = modalMode === "edit";
  const totalTokens = keys.reduce((sum, k) => sum + (k.tokensUsed || 0), 0);
  const totalPages = Math.ceil(keys.length / itemsPerPage);
  const pagedKeys = keys.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const assignedCount = ["chat", "image", "video"].filter(
    (t) => assignments[t]?.keyId,
  ).length;

  useEffect(() => {
    document.title = "Manage AI Keys | Mindora";
    window.scrollTo(0, 0);
    fetchKeys();
    fetchAssignments();
    fetchModels();
    fetchAlertSettings();
  }, []);

  /* Update filtered models when provider changes */
  useEffect(() => {
    if (!form.providerName) {
      setFilteredModels([]);
      return;
    }
    const models =
      allModels[form.providerName] || FALLBACK_MODELS[form.providerName] || [];
    setFilteredModels(models);
    setForm((prev) => ({
      ...prev,
      modelName: models.some((m) => m.id === prev.modelName)
        ? prev.modelName
        : "",
    }));
  }, [form.providerName, allModels]);

  const fetchKeys = () => {
    setLoading(true);
    axiosInstance
      .get("ai-keys")
      .then((res) => setKeys(res.data?.data ?? res.data ?? []))
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  };

  const fetchAssignments = () => {
    axiosInstance
      .get("ai-keys/assignments")
      .then((res) => {
        const list = res.data?.data ?? [];
        const map = {};
        list.forEach((a) => {
          map[a.keyType] = { keyId: a.keyId, key: a.key };
        });
        setAssignments(map);
      })
      .catch((err) => console.log(err));
  };

  const saveAssignment = (keyType, keyId) => {
    const prev = assignments;
    setAssignments((a) => ({
      ...a,
      [keyType]: { keyId, key: keys.find((k) => k.id === keyId) ?? null },
    }));
    axiosInstance
      .put("ai-keys/assignments", { keyType, keyId: keyId || null })
      .then(() => swal("", "Key assignment updated successfully!", "success"))
      .catch(() => {
        setAssignments(prev);
        swal("", "Failed to update assignment.", "error");
      });
  };

  const fetchModels = async () => {
    setModelsLoading(true);
    try {
      const res = await fetch("https://openrouter.ai/api/v1/models");
      const json = await res.json();
      const grouped = {};
      (json.data || []).forEach((m) => {
        // Skip deprecated / unavailable models (OpenRouter sets context_length to 0 for these)
        if (!m.context_length || m.context_length <= 0) return;
        const parts = m.id.split("/");
        if (parts.length < 2) return;
        const prefix = parts[0];
        // Strip provider prefix so native APIs (Google, OpenAI, Anthropic) receive the correct ID
        // e.g. "google/gemini-2.5-pro" → "gemini-2.5-pro"
        const nativeId = parts.slice(1).join("/");
        // Skip variant suffixes like ':thinking', ':free', ':extended' — not valid on native APIs
        if (nativeId.includes(":")) return;
        const provider = PROVIDERS.find((p) => p.prefix === prefix);
        if (provider) {
          if (!grouped[provider.value]) grouped[provider.value] = [];
          grouped[provider.value].push({
            id: nativeId,
            name: m.name || nativeId,
          });
        }
      });
      // Merge: always start with known-good fallback models, then append any additional ones from OpenRouter
      const merged = {};
      PROVIDERS.forEach((p) => {
        const fallback = FALLBACK_MODELS[p.value] || [];
        const extra = (grouped[p.value] || []).filter(
          (om) => !fallback.some((fm) => fm.id === om.id),
        );
        merged[p.value] = [...fallback, ...extra];
      });
      setAllModels(merged);
    } catch {
      setAllModels(FALLBACK_MODELS);
    } finally {
      setModelsLoading(false);
    }
  };

  const fetchAlertSettings = () => {
    // API: axiosInstance.get('/settings/ai-alert-recipients', { headers: { Authorization: `Bearer ${credential?.token}` } })
    //   .then(res => { setStaffList(res.data.staff); setAlertRecipients(res.data.selected) })
    setTimeout(() => {
      setStaffList(dummyStaff);
      setAlertRecipients([dummyStaff[0], dummyStaff[2]]);
    }, 200);
  };

  const saveAlertSettings = () => {
    setAlertSaving(true);
    // API: axiosInstance.post('/settings/ai-alert-recipients', { ids: alertRecipients.map(r => r.value) }, ...)
    setTimeout(() => {
      setAlertSaving(false);
      swal("", "Email alert recipients saved successfully!", "success");
    }, 400);
  };

  /* ─── Modal ─── */
  const openAdd = () => {
    setModalMode("add");
    setEditingKey(null);
    setForm(EMPTY_FORM);
    setErrors({});
    setShowKey(false);
    setShowModal(true);
  };

  const openEdit = (key) => {
    setModalMode("edit");
    setEditingKey(key);
    setForm({
      description: key.description,
      providerName: key.providerName,
      modelName: key.modelName,
      apiKey: "",
    });
    setErrors({});
    setShowKey(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingKey(null);
  };

  const validate = () => {
    const errs = {};
    if (!form.description.trim()) errs.description = "Key name is required.";
    if (!form.providerName) errs.providerName = "Please select a provider.";
    if (!form.modelName) errs.modelName = "Please select a model.";
    if (!isEdit && !form.apiKey.trim()) errs.apiKey = "API key is required.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setSubmitting(true);
    const req = isEdit
      ? axiosInstance.put(`ai-keys/${editingKey.id}`, form)
      : axiosInstance.post("ai-keys", form);
    req
      .then((res) => {
        fetchKeys();
        closeModal();
        swal(
          "",
          res?.data?.message ||
            (isEdit ? "Key updated successfully!" : "Key added successfully!"),
          "success",
        );
      })
      .catch((err) =>
        swal(
          "",
          err?.response?.data?.message || "Something went wrong.",
          "error",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  const handleDelete = (key) => {
    swal({
      title: `Delete "${key.description}"?`,
      text: "This action cannot be undone.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((will) => {
      if (will) {
        setLoading(true);
        axiosInstance
          .delete(`ai-keys/${key.id}`)
          .then((res) => {
            fetchKeys();
            fetchAssignments();
            swal(
              "",
              res?.data?.message || `"${key.description}" deleted.`,
              "success",
            );
          })
          .catch((err) =>
            swal(
              "",
              err?.response?.data?.message || "Failed to delete key.",
              "error",
            ),
          )
          .finally(() => setLoading(false));
      }
    });
  };

  /* ─── Render ─── */
  return (
    <>
      {loading && <Loader />}
      <ContainerDashboard
        title="Manage AI Keys"
        description="Configure and monitor AI provider API keys"
      >
        <section className="ps-dashboards p-4">
          {/* Info Banner */}
          <div
            className="rounded-4 p-4 mb-4 position-relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #A44CD7 0%, #0CACC2 100%)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: -30,
                right: -30,
                width: 150,
                height: 150,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.07)",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: -40,
                right: 160,
                width: 110,
                height: 110,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.05)",
              }}
            />
            <div className="position-relative d-flex align-items-center gap-3">
              <img
                height={40}
                src={aiKeyImg}
                alt="img"
                style={{ flexShrink: 0 }}
              />
              <div className="flex-grow-1">
                <h5 className="fw-bold mb-1" style={{ color: "#fff" }}>
                  AI Key Management
                </h5>
                <p
                  className="mb-0"
                  style={{ color: "rgba(255,255,255,0.82)", fontSize: 14 }}
                >
                  Manage your AI provider API keys, select the active key, and
                  monitor token usage per key.
                </p>
              </div>
              {checkAccess(11, 2) && (
                <button
                  type="button"
                  className="button btn-green"
                  style={{ flexShrink: 0 }}
                  onClick={openAdd}
                >
                  <i className="icon-plus me-2"></i>Add New Key
                </button>
              )}
            </div>
          </div>

          {/* Summary Stats */}
          {/* <div className="row g-3 mb-4">
                        {[
                            { label: 'Total Keys',        value: keys.length,                icon: 'bi bi-key',          color: '#A44CD7' },
                            { label: 'Assigned Features', value: `${assignedCount} / 3`,      icon: 'bi bi-plug-fill',    color: '#0CACC2' },
                            { label: 'Total Tokens Used', value: formatTokens(totalTokens),   icon: 'bi bi-bar-chart',    color: '#F9BE22' },
                        ].map((s, i) => (
                            <div className="col-md-4" key={i}>
                                <div className="rounded-4 p-3 d-flex align-items-center gap-3" style={{ background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                                    <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                        style={{ width: 48, height: 48, background: s.color + '1A' }}>
                                        <i className={s.icon} style={{ fontSize: 22, color: s.color }}></i>
                                    </div>
                                    <div>
                                        <p className="mb-0" style={{ fontSize: 11, color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</p>
                                        <h4 className="mb-0 fw-bold" style={{ color: '#1a1a2e' }}>{s.value}</h4>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div> */}

          {/* ─── Assignment Cards ─── */}
          <h6
            className="fw-bold mb-3"
            style={{ color: "#1a1a2e", fontSize: 16 }}
          >
            Key Assignments
            <span
              className="ms-2 text-muted fw-normal"
              style={{ fontSize: 13 }}
            >
              — select which key powers each feature
            </span>
          </h6>
          <div className="row g-3 mb-4">
            {[
              {
                keyType: "chat",
                label: "AI Chat",
                icon: "bi-chat-dots-fill",
                color: "#0CACC2",
                bg: "rgba(12,172,194,0.10)",
                border: "rgba(12,172,194,0.20)",
              },
              {
                keyType: "image",
                label: "Image Generation",
                icon: "bi-image-fill",
                color: "#A44CD7",
                bg: "rgba(164,76,215,0.10)",
                border: "rgba(164,76,215,0.20)",
              },
              {
                keyType: "video",
                label: "Video Generation",
                icon: "bi-film",
                color: "#F55145",
                bg: "rgba(245,81,69,0.10)",
                border: "rgba(245,81,69,0.20)",
              },
            ].map((tab) => {
              const asgn = assignments[tab.keyType];
              const assignedKey = asgn?.key;
              const assignedId = asgn?.keyId ?? "";
              const prov = assignedKey
                ? getProvider(assignedKey.providerName)
                : null;
              return (
                <div className="col-lg-4 col-md-6" key={tab.keyType}>
                  <div
                    className="rounded-4 p-4 h-100 d-flex flex-column"
                    style={{
                      background: "#fff",
                      boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
                      border: `1.5px solid ${tab.border}`,
                    }}
                  >
                    <div className="d-flex align-items-center gap-3 mb-3">
                      <div
                        className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: 46, height: 46, background: tab.bg }}
                      >
                        <i
                          className={`bi ${tab.icon}`}
                          style={{ fontSize: 20, color: tab.color }}
                        ></i>
                      </div>
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <h6
                          className="fw-bold mb-0"
                          style={{ color: "#1a1a2e" }}
                        >
                          {tab.label}
                        </h6>
                        {assignedKey ? (
                          <p
                            className="mb-0"
                            style={{
                              fontSize: 12,
                              color: prov?.color || tab.color,
                              fontWeight: 600,
                              wordBreak: "break-word",
                              lineHeight: 1.4,
                            }}
                          >
                            {assignedKey.providerName} ·{" "}
                            <span style={{ color: "#888", fontWeight: 400 }}>
                              {assignedKey.modelName}
                            </span>
                          </p>
                        ) : (
                          <p
                            className="mb-0"
                            style={{ fontSize: 12, color: "#bbb" }}
                          >
                            No key assigned
                          </p>
                        )}
                      </div>
                      {assignedKey && (
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{
                            width: 10,
                            height: 10,
                            background: "#19C37D",
                            boxShadow: "0 0 0 3px rgba(25,195,125,0.18)",
                          }}
                        />
                      )}
                    </div>
                    <select
                      className="form-select mt-auto"
                      style={{
                        borderRadius: 10,
                        fontSize: 12,
                        borderColor: assignedKey ? tab.color + "66" : "#dee2e6",
                        color: assignedKey ? "#1a1a2e" : "#aaa",
                        maxWidth: "100%",
                        fontFamily: "inherit",
                      }}
                      value={assignedId}
                      onChange={(e) =>
                        saveAssignment(
                          tab.keyType,
                          e.target.value ? Number(e.target.value) : null,
                        )
                      }
                    >
                      <option value="" style={{ fontSize: 12 }}>
                        — Not Assigned —
                      </option>
                      {keys.map((k) => {
                        const shortDesc =
                          k.description?.length > 20
                            ? k.description.slice(0, 20) + "…"
                            : k.description;
                        return (
                          <option
                            key={k.id}
                            value={k.id}
                            style={{ fontSize: 12 }}
                          >
                            {shortDesc} ({k.providerName})
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Keys Table */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6
              className="fw-bold mb-0"
              style={{ color: "#1a1a2e", fontSize: 16 }}
            >
              API Keys
              <span
                className="ms-2 badge rounded-pill"
                style={{
                  background: "#A44CD71A",
                  color: "#A44CD7",
                  fontSize: 12,
                }}
              >
                {keys.length}
              </span>
            </h6>
            <PerPageSelect
              itemsPerPage={itemsPerPage}
              setItemsPerPage={setItemsPerPage}
              setPage={setPage}
            />
          </div>

          <div
            style={{
              overflowX: "auto",
              width: "100%",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <table className="jean-table table" style={{ minWidth: "960px" }}>
              <tbody>
                {pagedKeys.map((key, idx) => {
                  const prov = getProvider(key.providerName);
                  return (
                    <tr key={key.id}>
                      {/* Serial */}
                      <td>
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                          style={{
                            width: 38,
                            height: 38,
                            background: "rgba(164,76,215,0.12)",
                            color: "#A44CD7",
                            fontSize: 13,
                          }}
                        >
                          {(page - 1) * itemsPerPage + idx + 1}
                        </div>
                      </td>

                      {/* Key name + date */}
                      <td>
                        <div>
                          <p
                            className="mb-0 fw-semibold"
                            style={{
                              color: "#1a1a2e",
                              wordBreak: "break-word",
                            }}
                          >
                            {key.description}
                          </p>
                          <p
                            className="mb-0"
                            style={{ fontSize: 12, color: "#999" }}
                          >
                            Added{" "}
                            {key.createdAt
                              ? new Date(key.createdAt).toLocaleDateString(
                                  "en-US",
                                )
                              : "—"}
                          </p>
                        </div>
                      </td>

                      {/* Provider */}
                      <td>
                        <div>
                          <p className="mb-1" style={labelStyle}>
                            Provider
                          </p>
                          <span
                            className="px-2 py-1 rounded-pill fw-semibold"
                            style={{
                              background: prov?.bg || "#f5eeff",
                              color: prov?.color || "#A44CD7",
                              fontSize: 12,
                            }}
                          >
                            {key.providerName || "—"}
                          </span>
                        </div>
                      </td>

                      {/* Model */}
                      <td>
                        <div>
                          <p className="mb-1" style={labelStyle}>
                            Model
                          </p>
                          <p
                            className="mb-0"
                            title={key.modelName}
                            style={{
                              fontSize: 13,
                              color: "#444",
                              wordBreak: "break-word",
                            }}
                          >
                            {key.modelName || "—"}
                          </p>
                        </div>
                      </td>

                      {/* Masked key */}
                      <td>
                        <div>
                          <p className="mb-1" style={labelStyle}>
                            API Key
                          </p>
                          <p
                            className="mb-0"
                            style={{
                              fontSize: 12,
                              color: "#888",
                              fontFamily: "monospace",
                              letterSpacing: 1,
                            }}
                          >
                            {key.apiKey || "—"}
                          </p>
                        </div>
                      </td>

                      {/* Tokens used */}
                      {/* <td>
                        <div>
                          <p className="mb-1" style={labelStyle}>
                            Tokens Used
                          </p>
                          <span
                            className="badge rounded-pill px-3 py-1 fw-semibold"
                            style={{
                              background:
                                key.tokensUsed > 100000
                                  ? "rgba(245,81,69,0.12)"
                                  : key.tokensUsed > 10000
                                  ? "rgba(249,190,34,0.15)"
                                  : "rgba(164,76,215,0.12)",
                              color:
                                key.tokensUsed > 100000
                                  ? "#F55145"
                                  : key.tokensUsed > 10000
                                  ? "#c8960a"
                                  : "#A44CD7",
                              fontSize: 13,
                            }}
                          >
                            {formatTokens(key.tokensUsed)}
                          </span>
                        </div>
                      </td> */}

                      {/* Status — commented out to keep row compact
                                            <td>
                                                <div>
                                                    <p className="mb-1" style={labelStyle}>Status</p>
                                                    {key.isActive ? (
                                                        <span className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill fw-semibold"
                                                            style={{ background: '#e8f8f0', color: '#1a9e5c', fontSize: 12 }}>
                                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#1a9e5c', display: 'inline-block' }}></span>
                                                            Active
                                                        </span>
                                                    ) : (
                                                        <span className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill"
                                                            style={{ background: '#f5f5f5', color: '#999', fontSize: 12 }}>
                                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ccc', display: 'inline-block' }}></span>
                                                            Inactive
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            */}

                      {/* Actions */}
                      <td className="pe-3">
                        <div className="d-flex justify-content-end gap-2 flex-wrap">
                          {checkAccess(11, 3) && (
                            <button
                              type="button"
                              className="btn fw-semibold"
                              style={{
                                background: "#f5eeff",
                                color: "#A44CD7",
                                borderRadius: 8,
                                padding: "5px 12px",
                                fontSize: 12,
                                border: "1px solid #e0c8ff",
                              }}
                              onClick={() => openEdit(key)}
                            >
                              <i className="bi bi-pencil me-1"></i>Edit
                            </button>
                          )}
                          {checkAccess(11, 4) && (
                            <button
                              type="button"
                              className="btn fw-semibold"
                              style={{
                                background: "#fff0f0",
                                color: "#e03131",
                                borderRadius: 8,
                                padding: "5px 12px",
                                fontSize: 12,
                                border: "1px solid #ffd0d0",
                              }}
                              onClick={() => handleDelete(key)}
                            >
                              <i className="icon-trash me-1"></i>Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {keys.length === 0 && !loading && (
                  <tr>
                    <td colSpan={8}>
                      <div className="alert alert-success ps-4 m-2">
                        No AI keys added yet!!
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            setPage={setPage}
            totalItems={keys.length}
            showingCount={pagedKeys.length}
          />

          {/* ─── Email Alerts Section ─── */}
          <div className="mt-4">
            <h6
              className="fw-bold mb-3"
              style={{ color: "#1a1a2e", fontSize: 16 }}
            >
              <i
                className="bi bi-envelope me-2"
                style={{ color: "#A44CD7" }}
              ></i>
              Email Alerts
            </h6>
            <div
              className="rounded-4 p-4"
              style={{
                background: "#fff",
                boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                border: "1px solid rgba(164,76,215,0.10)",
              }}
            >
              <div className="d-flex align-items-start gap-3 mb-4">
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(164,76,215,0.10)",
                  }}
                >
                  <i
                    className="bi bi-envelope"
                    style={{ fontSize: 20, color: "#A44CD7" }}
                  ></i>
                </div>
                <div>
                  <p
                    className="mb-0 fw-semibold"
                    style={{ color: "#1a1a2e", fontSize: 14 }}
                  >
                    AI Failure Notifications
                  </p>
                  <p
                    className="mb-0"
                    style={{ fontSize: 13, color: "#888", lineHeight: 1.6 }}
                  >
                    Select staff members who should receive an email alert
                    whenever the AI stops responding or encounters an error.
                  </p>
                </div>
              </div>

              <div className="row align-items-center g-3">
                <div className="col-md-9">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: 13, color: "#444" }}
                  >
                    Notify Staff Members
                  </label>
                  <Select
                    isMulti
                    options={staffList}
                    value={alertRecipients}
                    onChange={setAlertRecipients}
                    placeholder="Select staff to notify…"
                    formatOptionLabel={(opt) => (
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                          style={{
                            width: 28,
                            height: 28,
                            background: "rgba(164,76,215,0.12)",
                            color: "#A44CD7",
                            fontSize: 11,
                          }}
                        >
                          {opt.label.charAt(0)}
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              color: "#1a1a2e",
                              lineHeight: 1.2,
                            }}
                          >
                            {opt.label}
                          </div>
                          <div style={{ fontSize: 11, color: "#999" }}>
                            {opt.email}
                          </div>
                        </div>
                      </div>
                    )}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        borderRadius: 10,
                        borderColor: state.isFocused ? "#A44CD7" : "#dee2e6",
                        boxShadow: state.isFocused
                          ? "0 0 0 2px rgba(164,76,215,0.15)"
                          : "none",
                        minHeight: 44,
                        fontSize: 14,
                        "&:hover": { borderColor: "#A44CD7" },
                      }),
                      multiValue: (base) => ({
                        ...base,
                        background: "rgba(164,76,215,0.10)",
                        borderRadius: 8,
                      }),
                      multiValueLabel: (base) => ({
                        ...base,
                        color: "#A44CD7",
                        fontWeight: 600,
                        fontSize: 12,
                      }),
                      multiValueRemove: (base) => ({
                        ...base,
                        color: "#A44CD7",
                        borderRadius: "0 8px 8px 0",
                        "&:hover": {
                          background: "rgba(164,76,215,0.20)",
                          color: "#7a2fb0",
                        },
                      }),
                      option: (base, state) => ({
                        ...base,
                        background: state.isSelected
                          ? "rgba(164,76,215,0.10)"
                          : state.isFocused
                          ? "#faf5ff"
                          : "#fff",
                        color: "#1a1a2e",
                        padding: "8px 12px",
                      }),
                      menu: (base) => ({
                        ...base,
                        borderRadius: 12,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                        zIndex: 9999,
                      }),
                    }}
                  />
                  <p
                    className="mb-0 mt-2"
                    style={{ fontSize: 11, color: "#aaa" }}
                  >
                    {alertRecipients.length === 0
                      ? "No recipients selected — alerts will not be sent."
                      : `${alertRecipients.length} staff member${
                          alertRecipients.length > 1 ? "s" : ""
                        } will be notified.`}
                  </p>
                </div>
                <div className="col-md-3">
                  <button
                    type="button"
                    className="btn fw-semibold text-white"
                    style={{
                      borderRadius: 10,
                      background: "linear-gradient(135deg, #A44CD7, #0CACC2)",
                      border: "none",
                      fontSize: 13,
                      padding: "10px 0",
                      width: "50%",
                    }}
                    onClick={saveAlertSettings}
                    disabled={alertSaving}
                  >
                    {alertSaving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Saving…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-floppy me-2"></i>Save Alerts
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </ContainerDashboard>

      {/* Add / Edit Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.48)",
            zIndex: 99999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 520,
              background: "#fff",
              borderRadius: 20,
              overflow: "hidden",
              boxShadow: "0 8px 40px rgba(0,0,0,0.20)",
            }}
          >
            {/* Modal header */}
            <div
              className="d-flex align-items-center gap-3 px-4 py-3"
              style={{
                background: "linear-gradient(135deg, #A44CD7, #0CACC2)",
              }}
            >
              <i
                className={isEdit ? "icon-pencil" : "bi bi-key"}
                style={{ fontSize: 22, color: "#fff" }}
              ></i>
              <div className="flex-grow-1">
                <h6 className="mb-0 fw-bold text-white">
                  {isEdit
                    ? `Edit Key — ${editingKey?.description}`
                    : "Add New AI Key"}
                </h6>
                <p
                  className="mb-0"
                  style={{ fontSize: 12, color: "rgba(255,255,255,0.8)" }}
                >
                  {isEdit
                    ? "Update key details. Leave API key blank to keep existing."
                    : "Connect a new AI provider API key."}
                </p>
              </div>
              <button
                onClick={closeModal}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  color: "#fff",
                  fontSize: 16,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal body */}
            <div className="p-4">
              <div className="row g-3 mb-4">
                {/* Key Name */}
                <div className="col-12">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: 13 }}
                  >
                    Key Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    style={{ borderRadius: 10, fontSize: 14 }}
                    placeholder="e.g. Anthropic Production"
                    value={form.description}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, description: e.target.value }))
                    }
                  />
                  {errors.description && (
                    <p
                      className="mb-0 mt-1 text-danger"
                      style={{ fontSize: 12 }}
                    >
                      {errors.description}
                    </p>
                  )}
                </div>

                {/* Provider */}
                <div className="col-6">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: 13 }}
                  >
                    Provider <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    style={{ borderRadius: 10, fontSize: 14 }}
                    value={form.providerName}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        providerName: e.target.value,
                        modelName: "",
                      }))
                    }
                  >
                    <option value="">Select provider…</option>
                    {PROVIDERS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  {errors.providerName && (
                    <p
                      className="mb-0 mt-1 text-danger"
                      style={{ fontSize: 12 }}
                    >
                      {errors.providerName}
                    </p>
                  )}
                </div>

                {/* Model */}
                <div className="col-6">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: 13 }}
                  >
                    Model <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    style={{ borderRadius: 10, fontSize: 14 }}
                    value={form.modelName}
                    disabled={!form.providerName || modelsLoading}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, modelName: e.target.value }))
                    }
                  >
                    <option value="">
                      {modelsLoading
                        ? "Loading models…"
                        : !form.providerName
                        ? "Select provider first"
                        : "Select model…"}
                    </option>
                    {filteredModels.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                  {errors.modelName && (
                    <p
                      className="mb-0 mt-1 text-danger"
                      style={{ fontSize: 12 }}
                    >
                      {errors.modelName}
                    </p>
                  )}
                </div>

                {/* How to get your API key */}
                {!isEdit &&
                  form.providerName &&
                  (() => {
                    const guide = PROVIDER_STEPS[form.providerName];
                    if (!guide) return null;
                    return (
                      <div className="col-12">
                        <div
                          className="rounded-3 p-3"
                          style={{
                            background: guide.bg,
                            border: `1px solid ${guide.border}`,
                          }}
                        >
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <span style={{ fontSize: 16 }}>{guide.icon}</span>
                            <span
                              className="fw-bold"
                              style={{ fontSize: 13, color: guide.color }}
                            >
                              How to get your {form.providerName} API key
                            </span>
                            <a
                              href={guide.url}
                              target="_blank"
                              rel="noreferrer"
                              className="ms-auto d-inline-flex align-items-center gap-1"
                              style={{
                                fontSize: 11,
                                color: guide.color,
                                textDecoration: "none",
                                fontWeight: 600,
                              }}
                            >
                              <i
                                className="bi bi-box-arrow-up-right"
                                style={{ fontSize: 10 }}
                              ></i>
                              {guide.urlLabel}
                            </a>
                          </div>
                          <ol
                            className="mb-0 ps-3"
                            style={{
                              fontSize: 12,
                              color: "#444",
                              lineHeight: 1.7,
                            }}
                          >
                            {guide.steps.map((step, idx) => (
                              <li
                                key={idx}
                                style={{
                                  marginBottom:
                                    idx < guide.steps.length - 1 ? 2 : 0,
                                }}
                              >
                                {step}
                              </li>
                            ))}
                          </ol>
                        </div>
                      </div>
                    );
                  })()}

                {/* API Key */}
                <div className="col-12">
                  <label
                    className="form-label fw-semibold"
                    style={{ fontSize: 13 }}
                  >
                    API Key {!isEdit && <span className="text-danger">*</span>}
                  </label>
                  <div className="position-relative">
                    <input
                      type={showKey ? "text" : "password"}
                      className="form-control pe-5"
                      style={{ borderRadius: 10, fontSize: 14 }}
                      placeholder={
                        isEdit ? "Leave blank to keep existing key" : "sk-…"
                      }
                      value={form.apiKey}
                      autoComplete="new-password"
                      onChange={(e) =>
                        setForm((p) => ({ ...p, apiKey: e.target.value }))
                      }
                    />
                    <button
                      type="button"
                      style={{
                        position: "absolute",
                        right: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        color: "#999",
                        cursor: "pointer",
                        fontSize: 15,
                      }}
                      onClick={() => setShowKey((p) => !p)}
                    >
                      <i
                        className={`bi ${showKey ? "bi-eye-slash" : "bi-eye"}`}
                      ></i>
                    </button>
                  </div>
                  {errors.apiKey && (
                    <p
                      className="mb-0 mt-1 text-danger"
                      style={{ fontSize: 12 }}
                    >
                      {errors.apiKey}
                    </p>
                  )}
                  <p
                    className="mb-0 mt-1"
                    style={{ fontSize: 11, color: "#aaa" }}
                  >
                    {isEdit
                      ? "Leave blank to keep the existing key unchanged."
                      : "Stored securely — only the last 4 characters are visible after saving."}
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="d-flex gap-2 justify-content-end">
                <button
                  className="btn px-4"
                  style={{
                    borderRadius: 10,
                    background: "#f5f5f5",
                    color: "#555",
                    fontSize: 13,
                  }}
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button
                  className="btn px-4 fw-semibold text-white"
                  disabled={submitting}
                  style={{
                    borderRadius: 10,
                    background: "linear-gradient(135deg, #A44CD7, #0CACC2)",
                    fontSize: 13,
                    border: "none",
                  }}
                  onClick={handleSubmit}
                >
                  <i className="bi bi-check2 me-1"></i>
                  {submitting ? "Saving…" : isEdit ? "Save Changes" : "Add Key"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageAIKey;
